// @ts-nocheck
import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  AccordionDetails,
  IconButton,
  AccordionSummary,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import moment from 'moment';
import { useTranslation } from 'react-i18next';

import {
  FHButton,
  FHAccordion,
  FHItemSelectDialog,
  FHChip,
} from '../../../../../../components/base';
import SuggestedGoal from '../SuggestedGoal';
import TargetPracticed from '../target-practiced/TargetPracticed';
import { getThemeColor } from '../../../../../../utils/common';
import { capitalizeFirstLetter } from '../../../../../../helpers';
import { Themes } from '../../../../../../constants';
import { FORTA_BLUE, FORTA_GRAY4, FORTA_GRAY3 } from '../../../../../../utils/colors';
import { goalChipRenderer } from '../../../utils';
import { GoalTypes } from '../../../../../../components/layout/treatment-plan/goals/common/constants/general';
import PlusIcon from '../../../../../../components/svgs/PlusIcon';
import TrashIcon from '../../../../../../components/svgs/TrashIcon';
import LinkedGoalIcon from '../../../../../../components/svgs/LinkedGoalIcon';
import { GoalTimingField, TechniqueUsedField } from './components';
import { GOAL_INCORPORATED_FIELDS_SX } from './components/styles';

import type { TPGoalDataCollectionMethod } from '../../../../../../components/layout/treatment-plan/goals/common/constants/general';
import type {
  IOption,
  ISessionGoalTrailsData,
  ISessionGoalTiming,
  ISessionPracticedTargetTrial,
  ITargetTrialRateEvent,
  ITreatmentPlanGoal,
  ITreatmentPlanLinkedGoal,
} from '../../../../../../utils/types';
import type { Moment } from 'moment';

interface GoalPracticedProps {
  isEditable: boolean;
  sessionId?: string;
  trials: Array<ISessionPracticedTargetTrial>;
  goalsPracticedIdList: number[];
  goal?: ITreatmentPlanGoal;
  dataCollectionMethod: TPGoalDataCollectionMethod | null;
  savingFailed: boolean;
  savingInProgress: boolean;
  savedGoal?: ISessionGoalTrailsData;
  dateStart: Moment | undefined;
  expectedDuration: number | undefined;
  onAddSuggestedGoal: (goalId: number) => void;
  onDelete: () => void;
  onDeleteRateEvent: (trialId: number, rateEventId: number) => void;
  onUpdateRateEvent: (trialId: number, rateEvent: ITargetTrialRateEvent) => void;
  onUpdateTrial: (targetId: number, trialDataDiff: any) => void;
  onUpdateTechniques: (goalId: number, techniqueCodes: string[]) => void;
  onUpdateTimings: (goalId: number, timings: ISessionGoalTiming[]) => void;
  onDeleteTrial: (trial: ISessionPracticedTargetTrial) => void;
  onUpdateTargets: (targetIds: number[]) => void;
  colorTheme: Themes;
  useNewUi: boolean;
  techniqueOptions: IOption[];
  showError: boolean;
  expanded: boolean;
  onExpandedChange: (expanded: boolean) => void;
}

function GoalPracticed(props: GoalPracticedProps) {
  const theme = useTheme();
  const { t } = useTranslation('sessionNotes');
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const {
    sessionId,
    isEditable,
    trials,
    goalsPracticedIdList,
    goal,
    dataCollectionMethod,
    savedGoal,
    savingFailed,
    savingInProgress,
    dateStart,
    expectedDuration,
    onAddSuggestedGoal,
    onDelete,
    onUpdateTrial,
    onUpdateTechniques,
    onUpdateTimings,
    onDeleteTrial,
    onUpdateTargets,
    onDeleteRateEvent,
    onUpdateRateEvent,
    colorTheme,
    useNewUi,
    techniqueOptions,
    showError,
    expanded,
    onExpandedChange,
  } = props;

  const [isCounterFocused, setIsCounterFocused] = useState<boolean>(false);
  const [isAddTargetsDialogOpened, setIsAddTargetsDialogOpened] = useState<boolean>(false);
  const [doesUnpracticedTargetExist, setDoesUnpracticedTargetExist] = useState<boolean>(true);
  const [suggestedGoalList, setSuggestedGoalList] = useState<ITreatmentPlanLinkedGoal[]>([]);
  const [linkedBehaviorReductionGoalName, setLinkedBehaviorReductionGoalName] = useState<
    string | null
  >(null);
  const [expandedTargetIds, setExpandedTargetIds] = useState<number[]>([]);
  const goalId = goal?.id ?? null;
  const goalType = goal?.goal_type ?? savedGoal?.goal_type;
  const sessionStart = dateStart ? moment(dateStart) : undefined;

  useEffect(() => {
    if (!trials || !goal?.targets) return;
    setDoesUnpracticedTargetExist(
      goal?.targets
        ?.filter((target) => target?.is_active)
        .filter((target) => !trials.some((trial) => trial?.target?.id === target?.id))?.length > 0,
    );
  }, [trials, goal]);

  useEffect(() => {
    if (!goal?.linked_goals?.skill_acquisition) return;
    setSuggestedGoalList(
      goal?.linked_goals?.skill_acquisition?.filter(
        (linkedGoal: ITreatmentPlanLinkedGoal) => goalsPracticedIdList.indexOf(linkedGoal.id) < 0,
      ),
    );
  }, [goal, goalsPracticedIdList]);

  useEffect(() => {
    let linkedBehaviorReductionGoalName: string | null = null;
    if (savedGoal) {
      linkedBehaviorReductionGoalName =
        savedGoal.linked_goals?.behavior_reduction?.at(0)?.goal_name?.label ?? null;
    }
    if (!linkedBehaviorReductionGoalName) {
      linkedBehaviorReductionGoalName =
        goal?.linked_goals?.behavior_reduction?.at(0)?.goal_name?.label ?? null;
    }

    if (linkedBehaviorReductionGoalName) {
      setLinkedBehaviorReductionGoalName(linkedBehaviorReductionGoalName);
    }
  }, [goal, goalsPracticedIdList, savedGoal]);

  useEffect(() => {
    if (!useNewUi) return;
    setExpandedTargetIds((prev) => [
      ...prev.filter((targetId) => trials.some((trial) => trial?.target?.id === targetId)),
      ...trials
        .map((trial) => trial?.target?.id)
        .filter((targetId): targetId is number => Boolean(targetId))
        .filter((targetId) => !prev.includes(targetId)),
    ]);
  }, [trials, useNewUi]);

  useEffect(() => {
    if (!isEditable) {
      return;
    }

    const techniqueOptionCodes = techniqueOptions.map((techniqueOption) =>
      String(techniqueOption.id),
    );
    const selectedTechniqueCodes = savedGoal?.techniques ?? [];
    const nextTechniqueCodes = selectedTechniqueCodes.filter((techniqueCode) =>
      techniqueOptionCodes.includes(techniqueCode),
    );

    if (nextTechniqueCodes.length !== selectedTechniqueCodes.length) {
      if (goalId !== null) {
        onUpdateTechniques(goalId, nextTechniqueCodes);
      }
      return;
    }

    if (techniqueOptions.length === 1 && selectedTechniqueCodes.length === 0) {
      if (goalId !== null) {
        onUpdateTechniques(goalId, [String(techniqueOptions[0].id)]);
      }
    }
  }, [goalId, isEditable, onUpdateTechniques, savedGoal?.techniques, techniqueOptions]);

  const handleCounterFocused = (isFocused: boolean) => {
    setIsCounterFocused(isFocused);
  };

  const availableActiveTargets = goal?.targets
    ?.filter((target) => target?.is_active)
    .filter((target) => !trials.some((trial) => trial?.target?.id === target?.id));

  const handleTargetExpandedChange = (targetId: number, nextExpanded: boolean) => {
    setExpandedTargetIds((prev) =>
      nextExpanded
        ? [...prev, targetId]
        : prev.filter((expandedTargetId) => expandedTargetId !== targetId),
    );
  };

  return (
    <Box mb={useNewUi ? 2 : 1}>
      <FHAccordion
        colorTheme={Themes.Primary}
        defaultExpanded={useNewUi ? undefined : true}
        expanded={useNewUi ? expanded : undefined}
        onChange={useNewUi ? (_, nextExpanded) => onExpandedChange(nextExpanded) : undefined}
        sx={{
          boxShadow: 'none',
          ...(useNewUi && {
            border: { xs: 0, sm: `1px solid ${FORTA_GRAY3}` },
            borderRadius: { xs: 0, sm: '10px' },
            marginBottom: 0,
          }),
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon />}
          sx={{
            py: useNewUi ? { xs: 0, sm: 1.5 } : 2,
            px: useNewUi ? { xs: 0, sm: 2 } : 3,
            minHeight: useNewUi ? { xs: 'auto', sm: 48 } : undefined,
            '& .MuiAccordionSummary-content': {
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            },
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              mr: 3,
              gap: useNewUi ? 1.5 : { xs: 2, sm: 0 },
              flexDirection: useNewUi ? 'row' : { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: useNewUi
                  ? { xs: 'column-reverse', sm: 'row' }
                  : { xs: 'column', sm: 'row' },
                alignItems: { xs: 'flex-start', sm: 'center' },
                gap: { xs: 1, sm: 0 },
              }}
              data-testid={goal?.goal_name?.label}
            >
              <FHChip
                contentRenderer={() =>
                  goalChipRenderer(
                    {
                      ...goal,
                      goal_type: goalType,
                    },
                    Boolean(linkedBehaviorReductionGoalName),
                  )
                }
              />
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                <Typography
                  color={getThemeColor(colorTheme).primary}
                  sx={{
                    fontSize: '21px',
                    fontWeight: useNewUi ? 700 : 500,
                    lineHeight: '30px',
                    textAlign: 'left',
                    ml: useNewUi ? { xs: 0, sm: 1.5 } : { xs: 0, sm: 2 },
                  }}
                >
                  {capitalizeFirstLetter(
                    trials?.[0]?.target?.goal?.goal_name?.label
                      ? trials[0]?.target?.goal?.goal_name?.label
                      : goal?.goal_name?.label ?? savedGoal?.goal_name ?? '',
                  )}
                </Typography>
                {linkedBehaviorReductionGoalName && (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
                    <Box sx={{ ml: { xs: 0, sm: 2 } }}>
                      <LinkedGoalIcon color={FORTA_BLUE.primary} width={20} height={20} />
                    </Box>
                    <Typography
                      color={getThemeColor(colorTheme).primary}
                      sx={{
                        fontSize: '14px',
                        fontWeight: 700,
                        lineHeight: '21px',
                        textAlign: 'left',
                        ml: 1,
                      }}
                    >
                      {capitalizeFirstLetter(linkedBehaviorReductionGoalName)}
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>
          {isEditable && !(useNewUi && goalType === GoalTypes.BehaviorReduction) && (
            <IconButton
              data-testid={`${goal?.goal_name?.label}_trash`}
              sx={{
                width: { xs: 48, md: 64 },
                height: { xs: 30, md: 40 },
                borderRadius: '20px',
                backgroundColor: 'transparent',
                '&:disabled': {
                  opacity: 0.5,
                },
              }}
              disabled={savingInProgress || isCounterFocused}
              onClick={onDelete}
            >
              <TrashIcon color={FORTA_BLUE.primary} width={21} height={21} />
            </IconButton>
          )}
        </AccordionSummary>
        <AccordionDetails
          sx={{
            px: useNewUi ? { xs: 0, sm: 2 } : 3,
            pt: useNewUi ? { xs: 1, sm: 0 } : 0,
            pb: useNewUi ? { xs: 0, sm: 2 } : 3,
          }}
        >
          <Box sx={{ width: '100%', textAlign: 'left' }}>
            {useNewUi && (
              <Box sx={GOAL_INCORPORATED_FIELDS_SX}>
                <GoalTimingField
                  disabled={!isEditable}
                  isBehaviorReductionGoal={goalType === GoalTypes.BehaviorReduction}
                  sessionStart={sessionStart}
                  expectedDuration={expectedDuration}
                  timings={savedGoal?.timings ?? []}
                  showError={showError}
                  onChange={(timings) => {
                    if (goalId !== null) {
                      onUpdateTimings(goalId, timings);
                    }
                  }}
                />
                <TechniqueUsedField
                  disabled={!isEditable}
                  options={techniqueOptions}
                  selectedTechniqueCodes={savedGoal?.techniques ?? []}
                  showError={showError && (savedGoal?.techniques?.length ?? 0) === 0}
                  onChange={(techniqueCodes) => {
                    if (goalId !== null) {
                      onUpdateTechniques(goalId, techniqueCodes);
                    }
                  }}
                />
              </Box>
            )}
            <Box mb={2}>
              <Typography fontWeight="bold" color={getThemeColor(colorTheme).primary}>
                {t('goalsPracticed.targets')}
              </Typography>
              <Typography mt={0.5} color={FORTA_GRAY4}>
                {t('goalsPracticed.addOnlyToTheTargetsWorkedOnDuringTheSession')}
              </Typography>
            </Box>
            <Box>
              {trials.map((trial) => {
                const targetId = trial?.target?.id ?? null;
                if (targetId === null) return null;

                return (
                  <TargetPracticed
                    key={targetId}
                    index={(trial?.target?.order ?? 0) + 1}
                    trial={trial}
                    sessionId={sessionId}
                    goalType={goalType}
                    dataCollectionMethod={dataCollectionMethod}
                    isEditable={isEditable}
                    savingFailed={savingFailed}
                    savingInProgress={savingInProgress}
                    dateStart={dateStart}
                    expectedDuration={expectedDuration}
                    useNewUi={useNewUi}
                    canDelete={
                      !(
                        useNewUi &&
                        goalType === GoalTypes.BehaviorReduction &&
                        trial?.target?.is_active
                      )
                    }
                    expanded={!useNewUi || expandedTargetIds.includes(targetId)}
                    onExpandedChange={(nextExpanded) =>
                      handleTargetExpandedChange(targetId, nextExpanded)
                    }
                    onTrialDataChange={(trialDataDiff: any) =>
                      onUpdateTrial(targetId, trialDataDiff)
                    }
                    isCounterFocused={isCounterFocused}
                    onCounterFocus={handleCounterFocused}
                    onDelete={() => onDeleteTrial(trial)}
                    onDeleteRateEvent={(trialId: number, rateEventId: number) =>
                      onDeleteRateEvent(trialId, rateEventId)
                    }
                    onUpdateRateEvent={(trialId: number, rateEvent: ITargetTrialRateEvent) =>
                      onUpdateRateEvent(trialId, rateEvent)
                    }
                  />
                );
              })}
            </Box>
            {isEditable && (
              <Box sx={{ my: 3, display: 'flex', justifyContent: 'flex-end' }}>
                <FHButton
                  data-testid={`addButtonTarget${capitalizeFirstLetter(
                    goal?.goal_name?.label.split(' ')?.join(''),
                  )}`}
                  sx={{ border: `1px solid ${FORTA_BLUE.primary}` }}
                  startIcon={
                    <PlusIcon color={doesUnpracticedTargetExist ? FORTA_GRAY4 : FORTA_GRAY3} />
                  }
                  secondary
                  lightBackground={true}
                  onClick={() => setIsAddTargetsDialogOpened(true)}
                  disabled={!doesUnpracticedTargetExist}
                >
                  {t('goalsPracticed.addTarget')}
                </FHButton>
              </Box>
            )}

            {isEditable &&
              goalType === GoalTypes.BehaviorReduction &&
              suggestedGoalList?.length > 0 && (
                <>
                  <Box sx={{ my: 3 }}>
                    <Typography fontWeight="bold" color={getThemeColor(colorTheme).primary}>
                      {t('goalsPracticed.suggestedBehaviorReplacement')}
                    </Typography>
                    <Typography mt={0.5} color={FORTA_GRAY4}>
                      {t('goalsPracticed.suggestedBehaviorReplacementDescription')}
                    </Typography>
                  </Box>
                  <Grid container spacing={3}>
                    {suggestedGoalList.map((suggestedGoal) => (
                      <Grid item xs={12} sm={6} key={suggestedGoal.id}>
                        <SuggestedGoal
                          id={suggestedGoal.id}
                          label={capitalizeFirstLetter(suggestedGoal.goal_name?.label ?? '')}
                          onSelect={(id) => onAddSuggestedGoal(Number(id))}
                        />
                      </Grid>
                    ))}
                  </Grid>
                </>
              )}
          </Box>
        </AccordionDetails>
      </FHAccordion>
      {isEditable && (
        <FHItemSelectDialog
          width={isMobile ? '100%' : '50%'}
          title={`Add targets for ${goal?.goal_name?.label ?? ''}`}
          submitText="Add"
          submitButtonStyles={{ width: '97px' }}
          itemList={availableActiveTargets?.map((target) => ({
            id: target.id ?? 0,
            dataTestId: `sessionGoalsTrail-${capitalizeFirstLetter(
              target?.label?.split(' ')?.join(''),
            )}`,
            label:
              (
                goal?.targets?.findIndex((currentTarget) => currentTarget.id === target.id) + 1
              ).toString() +
              '. ' +
              capitalizeFirstLetter(target?.label),
            checked: availableActiveTargets?.length == 1,
          }))}
          onSave={(listOfTargetIdsToBeAdded) => {
            onUpdateTargets(listOfTargetIdsToBeAdded);
            setIsAddTargetsDialogOpened(false);
          }}
          onClose={() => {
            setIsAddTargetsDialogOpened(false);
          }}
          open={isAddTargetsDialogOpened}
        />
      )}
    </Box>
  );
}

export default GoalPracticed;
